/**
 * Single source of truth for the dropdown options the company-prefs
 * editor uses (admin Create / setup page, self-serve Onboarding,
 * customer Settings). Inlining these in three places kept drifting
 * out of sync, Onboarding said "$20-30 per celebration" while
 * Settings said "$50-$100 monthly" and admin had a third variant.
 *
 * Budget ranges below are MONTHLY PROGRAM BUDGET. They reflect real
 * Lembra unit economics: per-celebration spend is $99–$599, and a
 * 20–200-person buyer celebrates 1–10+ events/month, so monthly
 * spend lives in the hundreds-to-thousands. The prior $20–$100
 * brackets were sub-cake-cost, meaningless as a planning input.
 *
 * The FBT_PER_CELEBRATION_THRESHOLD is the Australian Tax Office
 * "minor benefits exemption" line: a single benefit must be under
 * $300 (GST-inclusive), provided infrequently and irregularly, to
 * sit outside Fringe Benefits Tax. Surfaced in the order flow as a
 * non-blocking warning when a celebration crosses $300.
 */

export const TEAM_SIZE_OPTIONS = [
  "1-10",
  "11-25",
  "26-50",
  "51-100",
  "100+",
] as const;

export const INDUSTRY_OPTIONS = [
  "Technology",
  "Finance",
  "Healthcare",
  "Education",
  "Retail",
  "Creative",
  "Other",
] as const;

/**
 * Monthly program budget brackets. Stored in companies.monthly_budget
 * verbatim. Use BUDGET_OPTIONS for the select control.
 *
 * Each bracket also carries a `maxPerCelebration` AUD value used by
 * the order flow to suggest cake tiers and warn on per-celebration
 * overspend. The mapping divides the monthly upper bound by ~3
 * (the working assumption for a typical 20–50-person company that
 * celebrates 2–4 events/month) and rounds to a clean number.
 */
export interface BudgetBracket {
  /** Display + stored value */
  value: string;
  /** Plain-text label */
  label: string;
  /** Upper bound of the bracket (AUD), null for the open-ended top tier */
  monthlyMaxAud: number | null;
  /** Soft cap the order flow uses for per-celebration suggestions */
  perCelebrationMaxAud: number | null;
}

export const BUDGET_BRACKETS: BudgetBracket[] = [
  {
    value: "Under $250 / month",
    label: "Under $250 / month",
    monthlyMaxAud: 250,
    perCelebrationMaxAud: 100,
  },
  {
    value: "$250–$500 / month",
    label: "$250–$500 / month",
    monthlyMaxAud: 500,
    perCelebrationMaxAud: 175,
  },
  {
    value: "$500–$1,000 / month",
    label: "$500–$1,000 / month",
    monthlyMaxAud: 1000,
    perCelebrationMaxAud: 250,
  },
  {
    value: "$1,000–$2,500 / month",
    label: "$1,000–$2,500 / month",
    monthlyMaxAud: 2500,
    perCelebrationMaxAud: 350,
  },
  {
    value: "$2,500–$5,000 / month",
    label: "$2,500–$5,000 / month",
    monthlyMaxAud: 5000,
    perCelebrationMaxAud: 500,
  },
  {
    value: "$5,000+ / month",
    label: "$5,000+ / month",
    monthlyMaxAud: null,
    perCelebrationMaxAud: 599,
  },
];

export const BUDGET_OPTIONS = BUDGET_BRACKETS.map((b) => b.value);

/**
 * ATO minor-benefits FBT exemption threshold. Per-benefit spend at or
 * above this triggers a warning in the order flow so the buyer can
 * make an informed choice about FBT exposure. Not a hard block —
 * occasional > $300 celebrations are still legal, just need
 * accounting awareness.
 */
export const FBT_PER_CELEBRATION_THRESHOLD_AUD = 300;

/**
 * Lookup helper: resolve a stored monthly_budget string back to its
 * bracket. Returns null for legacy values (e.g. "$20-30") that
 * predate the current option set so callers can degrade gracefully.
 */
export function bracketFor(stored: string | null | undefined): BudgetBracket | null {
  if (!stored) return null;
  return BUDGET_BRACKETS.find((b) => b.value === stored) ?? null;
}

/**
 * Sprint 2.5a /settings-fix, normalize a stored monthly_budget
 * value to one of the canonical BUDGET_OPTIONS brackets.
 *
 * Earlier versions of /settings used per-event price ranges
 * ("$50–$100", "$100–$250", etc.) which were sub-cake-cost and
 * meaningless as a planning input (per the BUDGET_BRACKETS comment
 * above). This helper maps those legacy ranges up to their closest
 * canonical MONTHLY PROGRAM BUDGET bracket.
 *
 * Unknown values return "" so the consumer's Select renders the
 * placeholder and the user re-picks.
 */
const MONTHLY_BUDGET_LEGACY_MAP: Record<string, string> = {
  "$50–$100": "Under $250 / month",
  "$100–$250": "Under $250 / month",
  "$250–$500": "$250–$500 / month",
  "$500–$1,000": "$500–$1,000 / month",
  "$1,000+": "$1,000–$2,500 / month",
};

export function normalizeMonthlyBudget(stored: string | null | undefined): string {
  if (!stored) return "";
  if (BUDGET_OPTIONS.includes(stored)) return stored;
  return MONTHLY_BUDGET_LEGACY_MAP[stored] ?? "";
}
